import { addDays } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/date-input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCall, useDispositions, useSetDisposition } from '@/hooks/useCalling'
import { useCallStore } from '@/stores/call.store'
import type { Disposition } from '@/types/api/calling'
import { cn } from '@/utils/cn'
import { toISODateString } from '@/utils/date'
import { getErrorMessage } from '@/utils/error-parser'

import { outcomeLabel } from '../callText'

/** Which dispositions fit the call's outcome. */
export function dispositionsFor(dispositions: Disposition[], answered: boolean): Disposition[] {
  return dispositions.filter(
    (d) => d.active && (d.appliesTo === 'ANY' || (d.appliesTo === 'CONNECTED') === answered)
  )
}

/**
 * Asks for the result of an ended call. Opens by itself once when a call ends; can be dismissed and
 * done later from the call history.
 */
export function DispositionDialog({ callId }: { callId: string }) {
  const close = useCallStore((s) => s.closeDisposition)
  const { data: call } = useCall(callId)
  const { data: dispositions = [] } = useDispositions(false, !!call)
  const setDisposition = useSetDisposition()
  const [selectedId, setSelectedId] = useState<string | null>(call?.dispositionId ?? null)
  const [note, setNote] = useState(call?.dispositionNote ?? '')
  const [followUpAt, setFollowUpAt] = useState(toISODateString(addDays(new Date(), 1)))

  useEffect(() => {
    if (call?.dispositionId) setSelectedId(call.dispositionId)
  }, [call?.dispositionId])

  const answered = call?.outcome === 'ANSWERED'
  const options = useMemo(() => dispositionsFor(dispositions, answered), [dispositions, answered])
  const selected = options.find((d) => d.id === selectedId) ?? null
  const noteMissing = !!selected?.requiresNote && note.trim() === ''

  const submit = () => {
    if (!selected || noteMissing) return
    setDisposition.mutate(
      {
        callId,
        data: {
          dispositionId: selected.id,
          note: note.trim() || undefined,
          followUpAt: selected.createsFollowUp && followUpAt ? followUpAt : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success('Disposition saved')
          close()
        },
        onError: (error) => toast.error(getErrorMessage(error, 'Could not save the disposition')),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>How did the call go?</DialogTitle>
          <DialogDescription>
            {call ? `${call.leadName ?? call.leadNumber ?? 'Call'} · ${outcomeLabel(call.outcome, call.direction)}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {options.length === 0 ? (
          <p className="text-sm text-neutral-500">No dispositions are set up for this kind of call yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Disposition">
            {options.map((d) => (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={selectedId === d.id}
                onClick={() => setSelectedId(d.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
                  selectedId === d.id
                    ? 'border-primary-500 bg-primary-50 font-semibold text-primary-800'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                )}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.colorHex ?? '#94a3b8' }} />
                {d.label}
              </button>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <label htmlFor="disposition-note" className="text-xs font-medium text-neutral-600">
            Note{selected?.requiresNote ? ' (required)' : ''}
          </label>
          <textarea
            id="disposition-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            placeholder="What was discussed?"
          />
        </div>

        {selected?.createsFollowUp && (
          <div className="space-y-1.5">
            <label htmlFor="disposition-follow-up" className="text-xs font-medium text-neutral-600">
              Follow up on
            </label>
            <DateInput id="disposition-follow-up" value={followUpAt} onChange={setFollowUpAt} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Later
          </Button>
          <Button onClick={submit} disabled={!selected || noteMissing || setDisposition.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
